import { useState } from 'react';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, TextField, MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import type { MissionControlEnvironment } from '../../types/environment';
import { shareDevBox } from '../../services/environments';

interface ShareDevBoxDialogProps {
  env: MissionControlEnvironment;
  onDismiss: () => void;
  onShared: () => void;
}

export default function ShareDevBoxDialog({ env, onDismiss, onShared }: ShareDevBoxDialogProps) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isValid = email.trim().endsWith('@networg.com') || email.trim().endsWith('@thenetw.org');

  async function handleShare() {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await shareDevBox(env.EnvironmentId, email.trim());
      setSuccess(`Shared with ${email.trim()}`);
      setEmail('');
      onShared();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share DevBox');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: 'Share DevBox',
        subText: `Share "${env.FriendlyName}" with another team member.`,
      }}
      modalProps={{ isBlocking: submitting }}
    >
      {error && <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError('')}>{error}</MessageBar>}
      {success && <MessageBar messageBarType={MessageBarType.success}>{success}</MessageBar>}

      <TextField
        label="User email"
        placeholder="colleague@networg.com"
        value={email}
        onChange={(_, v) => setEmail(v || '')}
        description="Must be a @networg.com or @thenetw.org address"
        errorMessage={email.trim() && !isValid ? 'Please enter a valid organization email' : undefined}
      />

      <DialogFooter>
        <PrimaryButton
          text={submitting ? 'Sharing…' : 'Share'}
          onClick={handleShare}
          disabled={submitting || !isValid || !email.trim()}
          onRenderIcon={submitting ? () => <Spinner size={SpinnerSize.xSmall} /> : undefined}
        />
        <DefaultButton text="Close" onClick={onDismiss} disabled={submitting} />
      </DialogFooter>
    </Dialog>
  );
}
